import { useState } from "react";

import { TIPOS_CANAL, type Canal, type TipoCanal } from "../../lib/config-alertas/tipos";
import type {
  CanalIn,
  CanalPatch,
  ConfigSmtp,
  ConfigWebhook,
  ConfigWhatsApp,
} from "../../lib/config-alertas/tipos";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Formulário controlado de canal de notificação (FE-6a — Tela 6). Cobre os DOIS
 * modos (espelha `FormularioConta`):
 *
 *  - CRIAÇÃO (`canal === undefined`): `tipo` (select), `nome`, config CONDICIONAL
 *    por tipo, `ativo`. A credencial é OBRIGATÓRIA (validação de borda bloqueia
 *    "Salvar"). Envia `config` completo no POST.
 *  - EDIÇÃO (`canal` presente): `tipo` DISABLED (imutável no backend). Os campos 🔒
 *    (`password`/`webhook_url`/`token`) começam VAZIOS com placeholder "deixe em
 *    branco para manter a credencial atual". No submit: cred vazia → OMITE `config`
 *    do PATCH (mantém a cifrada); cred preenchida → envia `config` (re-cifra).
 *
 * 🔴 SEGURANÇA DA CREDENCIAL (inviolável): o valor 🔒 vive SÓ no `useState` deste
 * componente e vai SÓ no corpo da mutação (POST/PATCH, HTTPS). NUNCA: console.*,
 * localStorage/sessionStorage, query string, nem em mensagem de erro renderizada.
 * Na edição, os campos 🔒 NÃO são pré-preenchidos (write-only não volta do backend).
 *
 * Componente PURO: não conhece o hook de mutação. O orquestrador (`SecaoCanais`)
 * injeta `aoCriar`/`aoEditar`, `salvando` e o `erro` normalizado. SÓ tokens CPMH.
 */

/** Idiomas e versões default do WhatsApp (mesmos defaults do backend Fase 4). */
const WHATSAPP_LANG_DEFAULT = "pt_BR";
const WHATSAPP_API_DEFAULT = "v21.0";

const PLACEHOLDER_MANTER = "deixe em branco para manter a credencial atual";

interface FormularioCanalProps {
  /** Canal em edição, ou `undefined` para criação. */
  canal?: Canal;
  /** Cria um novo canal (modo criação). */
  aoCriar: (corpo: CanalIn) => void;
  /** Edita o canal (modo edição). `corpo` já OMITE `config` quando não trocada. */
  aoEditar: (corpo: CanalPatch) => void;
  aoCancelar: () => void;
  salvando: boolean;
  erro: unknown;
}

export function FormularioCanal({
  canal,
  aoCriar,
  aoEditar,
  aoCancelar,
  salvando,
  erro,
}: FormularioCanalProps): JSX.Element {
  const isEdit = canal !== undefined;
  const tipoDisabled = isEdit;

  const [tipo, setTipo] = useState<TipoCanal>(canal?.tipo ?? TIPOS_CANAL[0].valor);
  const [nome, setNome] = useState(canal?.nome ?? "");
  const [ativo, setAtivo] = useState(canal?.ativo ?? true);
  const [erroBorda, setErroBorda] = useState<string | null>(null);

  // ── Campos de config (write-only). Na edição começam VAZIOS — a credencial 🔒
  //    nunca volta do backend; um campo 🔒 vazio no submit = "manter a atual". ──
  // SMTP
  const [host, setHost] = useState("");
  const [port, setPort] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remetente, setRemetente] = useState("");
  const [smtpDestinatarios, setSmtpDestinatarios] = useState("");
  // Webhook (teams/slack)
  const [webhookUrl, setWebhookUrl] = useState("");
  // WhatsApp
  const [token, setToken] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [waDestinatarios, setWaDestinatarios] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [languageCode, setLanguageCode] = useState(WHATSAPP_LANG_DEFAULT);
  const [apiVersion, setApiVersion] = useState(WHATSAPP_API_DEFAULT);

  /** Quebra um campo de e-mails/telefones separados por vírgula em lista limpa. */
  function listaDe(texto: string): string[] {
    return texto
      .split(",")
      .map((item) => item.trim())
      .filter((item) => item !== "");
  }

  /**
   * O canal tem a credencial 🔒 preenchida neste form? (host/url/token conforme o
   * tipo). Na criação isso precisa ser `true`; na edição, `false` = manter a atual.
   */
  function credencialPreenchida(): boolean {
    if (tipo === "smtp") return password.trim() !== "";
    if (tipo === "teams" || tipo === "slack") return webhookUrl.trim() !== "";
    return token.trim() !== "";
  }

  /**
   * Monta o `config` a partir do estado atual. Só é chamado quando há credencial
   * (criação sempre; edição só quando o usuário decidiu trocar).
   */
  function montarConfig(): ConfigSmtp | ConfigWebhook | ConfigWhatsApp {
    if (tipo === "smtp") {
      return {
        host: host.trim(),
        port: Number(port),
        username: username.trim(),
        password,
        remetente: remetente.trim(),
        destinatarios: listaDe(smtpDestinatarios),
      };
    }
    if (tipo === "teams" || tipo === "slack") {
      return { webhook_url: webhookUrl.trim() };
    }
    return {
      token,
      phone_number_id: phoneNumberId.trim(),
      destinatarios: listaDe(waDestinatarios),
      template_name: templateName.trim(),
      language_code: languageCode.trim(),
      api_version: apiVersion.trim(),
    };
  }

  /**
   * Validação de borda dos campos OBRIGATÓRIOS na CRIAÇÃO (a credencial completa é
   * exigida). Devolve a mensagem do primeiro campo faltante, ou `null` se OK.
   */
  function validarCriacao(): string | null {
    if (nome.trim() === "") return "Informe o nome do canal.";
    if (tipo === "smtp") {
      if (host.trim() === "") return "Informe o host SMTP.";
      if (port.trim() === "" || Number.isNaN(Number(port)))
        return "Informe uma porta SMTP válida.";
      if (username.trim() === "") return "Informe o usuário SMTP.";
      if (password.trim() === "") return "Informe a senha SMTP.";
      if (remetente.trim() === "") return "Informe o remetente.";
      if (listaDe(smtpDestinatarios).length === 0)
        return "Informe ao menos um destinatário.";
      return null;
    }
    if (tipo === "teams" || tipo === "slack") {
      if (webhookUrl.trim() === "") return "Informe a URL do webhook.";
      return null;
    }
    // whatsapp
    if (token.trim() === "") return "Informe o token do WhatsApp.";
    if (phoneNumberId.trim() === "") return "Informe o phone number id.";
    if (listaDe(waDestinatarios).length === 0)
      return "Informe ao menos um destinatário.";
    if (templateName.trim() === "") return "Informe o nome do template.";
    return null;
  }

  function submeter(): void {
    if (nome.trim() === "") {
      setErroBorda("Informe o nome do canal.");
      return;
    }

    if (!isEdit) {
      // CRIAÇÃO: credencial completa é obrigatória.
      const faltante = validarCriacao();
      if (faltante !== null) {
        setErroBorda(faltante);
        return;
      }
      setErroBorda(null);
      aoCriar({ tipo, nome: nome.trim(), config: montarConfig(), ativo });
      return;
    }

    // EDIÇÃO: `tipo` não vai no PATCH. `config` só entra se a credencial 🔒 foi
    // preenchida (decisão explícita de trocar); vazia = manter a atual (omite).
    setErroBorda(null);
    const corpo: CanalPatch = { nome: nome.trim(), ativo };
    if (credencialPreenchida()) {
      corpo.config = montarConfig();
    }
    aoEditar(corpo);
  }

  return (
    <div className="flex max-w-lg flex-col gap-4 rounded-md border border-platinum bg-snow p-6 font-corpo text-base text-off-black">
      <h2 className="font-titulo text-xl text-off-black">
        {isEdit ? "Editar canal" : "Novo canal"}
      </h2>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">Tipo</span>
        <select
          value={tipo}
          disabled={tipoDisabled}
          onChange={(evento) => setTipo(evento.target.value as TipoCanal)}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black disabled:opacity-60"
        >
          {TIPOS_CANAL.map((opcao) => (
            <option key={opcao.valor} value={opcao.valor}>
              {opcao.rotulo}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1">
        <span className="font-subtitulo">Nome</span>
        <input
          type="text"
          value={nome}
          onChange={(evento) => setNome(evento.target.value)}
          className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
        />
      </label>

      {tipo === "smtp" && (
        <>
          <CampoTexto rotulo="Host" valor={host} aoMudar={setHost} />
          <CampoTexto
            rotulo="Porta"
            valor={port}
            aoMudar={setPort}
            tipoInput="number"
          />
          <CampoTexto rotulo="Usuário" valor={username} aoMudar={setUsername} />
          <CampoTexto
            rotulo="Senha"
            valor={password}
            aoMudar={setPassword}
            tipoInput="password"
            placeholder={isEdit ? PLACEHOLDER_MANTER : undefined}
          />
          <CampoTexto
            rotulo="Remetente"
            valor={remetente}
            aoMudar={setRemetente}
          />
          <CampoTexto
            rotulo="Destinatários (separados por vírgula)"
            valor={smtpDestinatarios}
            aoMudar={setSmtpDestinatarios}
          />
        </>
      )}

      {(tipo === "teams" || tipo === "slack") && (
        <CampoTexto
          rotulo="URL do webhook"
          valor={webhookUrl}
          aoMudar={setWebhookUrl}
          tipoInput="password"
          placeholder={isEdit ? PLACEHOLDER_MANTER : undefined}
        />
      )}

      {tipo === "whatsapp" && (
        <>
          <CampoTexto
            rotulo="Token"
            valor={token}
            aoMudar={setToken}
            tipoInput="password"
            placeholder={isEdit ? PLACEHOLDER_MANTER : undefined}
          />
          <CampoTexto
            rotulo="Phone number id"
            valor={phoneNumberId}
            aoMudar={setPhoneNumberId}
          />
          <CampoTexto
            rotulo="Destinatários (separados por vírgula)"
            valor={waDestinatarios}
            aoMudar={setWaDestinatarios}
          />
          <CampoTexto
            rotulo="Nome do template"
            valor={templateName}
            aoMudar={setTemplateName}
          />
          <CampoTexto
            rotulo="Idioma"
            valor={languageCode}
            aoMudar={setLanguageCode}
          />
          <CampoTexto
            rotulo="Versão da API"
            valor={apiVersion}
            aoMudar={setApiVersion}
          />
        </>
      )}

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={ativo}
          onChange={(evento) => setAtivo(evento.target.checked)}
        />
        <span className="font-subtitulo">Ativo</span>
      </label>

      {erroBorda !== null && (
        <p role="alert" className="font-corpo text-base text-flame">
          {erroBorda}
        </p>
      )}

      {erro !== null && erro !== undefined && (
        <p role="alert" className="font-corpo text-base text-flame">
          {mensagemErroCanal(erro)}
        </p>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={aoCancelar}
          disabled={salvando}
          className="rounded-md border border-platinum bg-snow px-4 py-1 text-off-black disabled:opacity-60"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={submeter}
          disabled={salvando}
          className="rounded-md border border-flame bg-flame px-4 py-1 text-snow disabled:opacity-60"
        >
          {salvando ? "Salvando…" : "Salvar"}
        </button>
      </div>
    </div>
  );
}

interface CampoTextoProps {
  rotulo: string;
  valor: string;
  aoMudar: (valor: string) => void;
  tipoInput?: "text" | "number" | "password";
  placeholder?: string | undefined;
}

/** Campo de texto rotulado reutilizável (label + input controlado). */
function CampoTexto({
  rotulo,
  valor,
  aoMudar,
  tipoInput = "text",
  placeholder,
}: CampoTextoProps): JSX.Element {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-subtitulo">{rotulo}</span>
      <input
        type={tipoInput}
        value={valor}
        placeholder={placeholder}
        onChange={(evento) => aoMudar(evento.target.value)}
        className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
      />
    </label>
  );
}

/**
 * Traduz o erro de uma mutação de canal numa mensagem ESTÁVEL e GENÉRICA. 409
 * (nome duplicado) e 422 (validação — ex.: webhook não-https) recebem mensagens
 * claras; demais reusam a mensagem genérica do `ErroHttp`. NUNCA ecoa o corpo do
 * servidor (que poderia conter a config). Erro não-`ErroHttp` → fallback.
 */
function mensagemErroCanal(erro: unknown): string {
  if (erro instanceof ErroHttp) {
    if (erro.status === 409) {
      return "Já existe um canal com este nome.";
    }
    if (erro.status === 422) {
      return "Dados inválidos. Verifique os campos e tente novamente.";
    }
    return erro.message;
  }
  return "Não foi possível salvar o canal.";
}
